import React, { useRef, useEffect, useState } from 'react';
import PropertyBase, {PropertyDataName} from './PropertyBase';
import TextVariables from '../../classes/TextVariables';
import DropDownLayer from '../layers/DropdownLayer';
import Input from '../../classes/Input'
import DomUtils from '../../classes/utilities/DomUtils';
import { DropDownList } from '../layout/DropDownList';
import AnalyticsManager from '../../classes/AnalyticsManager';
import { KeyCodes } from '../../interfaces/Input';

/**
 *
 *
 * @export
 * @param {*} props
 * @returns
 */
export default function StringProperty(props)
{
	const inputRef = useRef();
	const wrapperRef = useRef();

	const [isFocused, setIsFocused] = useState(false);
	const [autocompleteOptions, setAutocompleteOptions] = useState(null);
	const [currentWord, setCurrentWord] = useState(null);

	const contentHeightLimit = useRef(200);
	const rootRect = useRef(null);
	const drawParams = useRef(null);

	useEffect(() =>
	{
		if (inputRef.current && props.autoFocus)
		{
			inputRef.current.focus();
		}
	}, [props.autoFocus]);

	useEffect(() =>
	{
		if (isFocused)
		{
			inputRef.current.setSelectionRange(0, inputRef.current.value.length);
		}
	}, [isFocused]);

	useEffect(() =>
	{
		if (currentWord === null)
		{
			closeList();
			return;
		}

		const variables = TextVariables.autoComplete(currentWord, props.includePartsVariables);
		if (variables && variables.length > 0)
		{
			let dropDownOptions = [];
			for (let vari of variables)
			{
				dropDownOptions.push({ display: vari, value: vari });
			}
			setAutocompleteOptions(dropDownOptions);
		}
		else if (autocompleteOptions && autocompleteOptions.length > 0)
		{
			setAutocompleteOptions(null);
		}
	}, [currentWord]);

	useEffect(() =>
	{
		if (!autocompleteOptions || autocompleteOptions.length <= 0)
		{
			closeList();
		}
		else
		{
			const scrollOffset = DomUtils.getScrollOffsets(inputRef.current.closest('.listViewContainer'));
			rootRect.current = { ...DomUtils.getDomElementRect(inputRef.current) };
			rootRect.current.bottom -= scrollOffset.y;
			rootRect.current.left -= scrollOffset.x;

			let contentMinimumHeight = Math.min(3, autocompleteOptions.length) * 20;
			if (props.searchable)
				contentMinimumHeight += 20;
			if (props.decorators)
				contentMinimumHeight += 20;

			var coverRect = DomUtils.getDomElementRect(document.getElementById('workspaceRoot'));
			if (!coverRect) coverRect = { yMax: 949 }

			contentHeightLimit.current = coverRect.yMax - rootRect.current.yMax;
			contentHeightLimit.current = /*contentHeightLimit.current < 40 ? 40 : */contentHeightLimit.current > 250 ? 250 : contentHeightLimit.current;

			drawParams.current = {
				left: (rootRect.current.left - 1) + "px",
				width: (rootRect.current.width) + "px",
			};

			if (rootRect.current.bottom + contentMinimumHeight > coverRect.yMax)
			{
				drawParams.current.inverse = true;
				drawParams.current.top = (rootRect.current.bottom + 2 - rootRect.current.height - contentMinimumHeight) + "px";
				contentHeightLimit.current = contentMinimumHeight;
			}
			else
			{
				drawParams.current.top = (rootRect.current.bottom - 2) + "px";
			}

			let dropdownList = buildDropdownList();

			document.addEventListener('mousedown', testClickOut);
			document.addEventListener('keyup', testKeyOut);

			if (dropdownList)
			{
				DropDownLayer.setDropdownList(dropdownList);
			}
		}
	}, [autocompleteOptions]);

	const readonly = props.readOnly ? props.readOnly : false;
	const allowedKeys = props.allowedKeys ? props.allowedKeys : "";

	const dataName = StringPropertyDataName(props["data-name"]);

	function testClickOut(e)
	{
		const nearestRoot = e.target?.closest(".dropDownListWrapper");
		if (nearestRoot !== wrapperRef.current)
		{
			closeList();
		}
	}

	function testKeyOut(e)
	{
		if (e.keyCode === KeyCodes.ESCAPE)
		{
			closeList();
		}
	}

	function closeList()
	{
		document.removeEventListener('mousedown', testClickOut);
		document.removeEventListener('keyup', testKeyOut);
		DropDownLayer.setDropdownList(null);
		setAutocompleteOptions(null);
		setCurrentWord(null);
	}

	function buildDropdownList()
	{
		if (!rootRect.current) return;
		return <div className={"dropDownListWrapper"}
			ref={wrapperRef}
			data-name={"drop-down-list-wrapper"}
			style={{
				...drawParams.current,
				zIndex: 100000000,
			}}
		>
			<DropDownList
				data-name={props["data-name"] + "-options-container"}
				// searchable={props.searchable}
				options={autocompleteOptions}
				selected={props.value}
				onItemToggled
				onSelectionChanged={(selection) =>
				{
					applySelectionToText(selection);
					closeList();
				}}
				maxHeight={contentHeightLimit.current}
				decorators={props.decorators}
				inverse={drawParams.current ? drawParams.current.inverse ? drawParams.current.inverse : false : false}
			/>
		</div>
	}

	function applySelectionToText(selection)
	{
		let textObject = TextVariables.breakToWords(props.value);
		let wordIndex = 0;
		for (wordIndex; wordIndex < textObject.words.length; wordIndex++)
		{
			if (textObject.words[wordIndex] === currentWord)
				break;
		}

		textObject.words[wordIndex] = selection;

		if (props.onChangeCallback)
			props.onChangeCallback(TextVariables.buildFromWords(textObject.words, textObject.splitters));

		closeList();
		inputRef.current.focus();
	}

	return (
		<PropertyBase {...props}
			data-name={dataName + "-root"}
		>
			<div className={props.inputClassName ? props.inputClassName : "inputRoot"}
				disabled={readonly}
			>
				<input className={"textField"}
					data-name={dataName + "-input"}
					data-characterlimit={props.characterLimit ? "true" : "false"}
					data-hoverbackground={props.enabledHoverBackground === false ? "false" : "true"}
					data-focuswithin={props.enableFocusOutline === false ? "false" : "true"}
					maxLength={props.characterLimit ? props.characterLimit : 524288}
					ref={inputRef}
					placeholder={props.placeholder !== undefined ? props.placeholder : props.value}
					value={props.multivalues ? "~" : props.value}
					readOnly={readonly}
					focused={props.focused}
					autoFocus={props.autoFocus}
					style={props.style}
					draggable={true}
					disabled={props.disabled}
					onDragStart={(e) =>
					{
						if (!props.allowTextDrag)
							e.preventDefault()
					}}
					onDrop={(e) =>
					{
						if (!props.allowTextDrag)
						{
							e.preventDefault()
						}
					}}
					onChange={(e) =>
					{
						if (props.allowTextVariables)
						{
							setCurrentWord(TextVariables.getCurrentWord(e.target.value, e.target.selectionStart));
						}

						if (props.onChangeCallback)
							props.onChangeCallback(e.target.value, e);
					}}
					onBlur={(e) =>
					{
						if (autocompleteOptions && autocompleteOptions.length > 0) return;
						if (!readonly && props.onCommitCallback)
						{
							AnalyticsManager.managedEvent('propertyChanged', 'propertyId', e.target.dataset.name);
							const valueToCommit =
								e.target.value !== "" ? e.target.value :
									props.value !== undefined ? props.value :
										e.target.value;

							props.onCommitCallback(valueToCommit);
						}

						setIsFocused(false);
						if (props.onBlur) { props.onBlur(inputRef.current); }
					}}
					onFocus={(e) =>
					{
						setIsFocused(true);
						if (props.onFocus) { props.onFocus(inputRef.current); }
						if (!readonly && props.onFocusCallback)
							props.onFocusCallback();
					}}
					onKeyPress={(e) =>
					{
						if (e.key === "Enter" || e.keyCode === KeyCodes.ESCAPE)
						{
							if (props.onEscape && e.keyCode === KeyCodes.ESCAPE)
							{
								props.onEscape();
							}
							else if (e.key === "Enter")
							{
								if(props.onEnter) props.onEnter();
								if (props.onCommitCallback)
								{
									AnalyticsManager.managedEvent('propertyChanged', 'propertyId', e.target.dataset.name);
									if(props.blurOnEnter)
									{
										e.target.blur(e.target.value);
									}
									else
									{
										props.onCommitCallback(e.target.value);
									}
								}
							}
							else
							{
								e.target.blur(e.target.value);
							}
						}
						else if (allowedKeys.length && !allowedKeys.includes(e.key))
						{
							e.preventDefault();
						}
					}}
				/>
				{
					props.characterLimit ?
						<label className={"inputStringCharacterLimit"}>
							{`${props.value.length}/${props.characterLimit}`}
						</label>
						: <></>
				}
			</div>
		</PropertyBase>
	);
}

export function StringPropertyDataName(dataName, suffix = "")
{
	return PropertyDataName(dataName, "string-property", suffix)
}
